import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import Alert from "@mui/material/Alert";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import CallerImage from "@components/CallerImage";
import { Companion as CompanionModel } from "@models/companion";
import AvatarTimeout from "@components/AvatarTimeout";

const AUDIO_FILE_PATH = `${process.env.PUBLIC_URL}/audio/phone-outgoing-call.mp3`;

const Loading: FC<{ companion?: CompanionModel }> = ({ companion }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const outputVolume = useSelector(
    (state: RootState) => state.avatar.outputVolume,
  );

  useEffect(() => {
    const audio = new Audio(AUDIO_FILE_PATH);
    audio.loop = true;
    audioRef.current = audio;
    void audio.play();
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = outputVolume ?? 1;
    }
  }, [outputVolume]);

  return (
    <Stack justifyContent="center">
      <Stack
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        height="calc(95dvh - 100px)"
        maxHeight="600px"
        color="#fff"
      >
        <Typography
          fontSize={{ xs: "30px", sm: "40px" }}
          lineHeight="56px"
          fontWeight="400"
        >
          {companion?.name || ""}
        </Typography>

        <CallerImage src={companion?.avatar?.imageUrl || ""} alt="" />

        <Typography fontSize="24px" lineHeight="36px" fontWeight="400">
          {t("companion.loadingMessage")}
        </Typography>
      </Stack>
    </Stack>
  );
};

const LimitReachedDialog: FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>{t("companion.limitReachedDialog.title")}</DialogTitle>
      <DialogContent>{t("companion.limitReachedDialog.body")}</DialogContent>

      <DialogActions sx={{ padding: "16px" }}>
        <Button variant="contained" onClick={onClose}>
          {t("companion.limitReachedDialog.okButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LIMIT_CHECK_INTERVAL = 1000;

const Companion: FC<
  PropsWithChildren<{
    showLoading: boolean;
    companion: CompanionModel | undefined;
    startTime: Date | null;
    remainingTalkSeconds: number | null;
    onClose: () => void;
    onTimeout: () => void;
  }>
> = ({
  children,
  showLoading,
  companion,
  startTime,
  remainingTalkSeconds,
  onClose,
  onTimeout,
}) => {
  const { t } = useTranslation();
  const micNotFound = useSelector(
    (state: RootState) => state.microphone.micNotFound,
  );
  const micPermissionDenied = useSelector(
    (state: RootState) => state.microphone.micPermissionDenied,
  );
  const { ready } = useSelector((state: RootState) => state.avatar);
  const [limitReached, setLimitReached] = useState<boolean>(false);
  const limitCheckInterval = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  const error = useMemo(() => {
    switch (true) {
      case micNotFound:
        return t("errors.microphoneNotConnected");
      case micPermissionDenied:
        return t("errors.microphonePermissionDenied");
      default:
        return null;
    }
  }, [micNotFound, micPermissionDenied, t]);

  useEffect(() => {
    if (limitCheckInterval.current) {
      clearInterval(limitCheckInterval.current);
      limitCheckInterval.current = null;
    }

    if (!startTime) {
      return;
    }

    // Unlimited plan: parent passes null so we do not interpret negative
    // "remaining" (limit 0 − used) as a timed cap.
    if (remainingTalkSeconds == null) {
      setLimitReached(false);
      return;
    }

    if (remainingTalkSeconds <= 0) {
      setLimitReached(true);
      return;
    }

    setLimitReached(false);

    limitCheckInterval.current = setInterval(() => {
      if (
        new Date() >=
        new Date(startTime.getTime() + remainingTalkSeconds * 1000)
      ) {
        setLimitReached(true);
        if (limitCheckInterval.current) {
          clearInterval(limitCheckInterval.current);
          limitCheckInterval.current = null;
        }
      }
    }, LIMIT_CHECK_INTERVAL);

    return () => {
      if (limitCheckInterval.current) {
        clearInterval(limitCheckInterval.current);
        limitCheckInterval.current = null;
      }
    };
  }, [startTime, remainingTalkSeconds]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
      {/*ready && <AvatarTimeout onTimeout={onTimeout} />*/}
      {showLoading && !ready && !limitReached ? (
        <Box position="absolute">
          <Loading companion={companion} />
        </Box>
      ) : null}

      <LimitReachedDialog open={limitReached} onClose={onClose} />

      {!limitReached && children}
    </>
  );
};

export default Companion;
